<template>
    <asli-form @submitted="submit">
        <asli-section-header class="mt-8 mb-4">Marketing Preferences</asli-section-header>
        <asli-radio
            id="is_marketing_allowed"
            name="is_marketing_allowed"
            rules="required:marketing_preferences"
            v-model="marketingPreferences.allow_general_marketing"
            label="From time to time we would like to contact you with details of products and services provided by us and the An Post group of companies by Email, SMS, Post, Phone. Is this OK? (You can withdraw your consent at any time.)"
            :options="lists?.yes_no"
            :is-yes-no-option="true"
            tooltip-text="You can withdraw your consent at any time by Phone, Post, or Email or through our website."
            return-boolean
        />
        <form-transition :become-visible-when="marketingPreferences.allow_general_marketing === false">
            <asli-radio
                v-if="!marketingPreferences.allow_general_marketing"
                id="is_product_marketing_allowed"
                name="is_product_marketing_allowed"
                rules="required:marketing_preferences"
                v-model="marketingPreferences.allow_product_marketing"
                label="Would you like to only receive details and/or special offers in relation to our Home Insurance by Email, SMS, Post or Phone? Is this ok? (You can withdraw consent at any time.)"
                :options="lists?.yes_no"
                :is-yes-no-option="true"
                tooltip-text="You can withdraw your consent at any time by Phone, Post, or Email or through our website."
                return-boolean
            />
        </form-transition>
        <form-transition
            :become-visible-when="
                marketingPreferences.allow_general_marketing === true ||
                (marketingPreferences.allow_general_marketing === false &&
                    marketingPreferences.allow_product_marketing !== null)
            "
        >
            <asli-section-header class="my-[15px]">Contact Channels</asli-section-header>
            <p class="text-sm mb-[15px]">Please select which channels you would like us to use to contact you:</p>
            <div class="bg-white rounded overflow-hidden shadow mb-[15px]">
                <table class="w-full text-sm">
                    <thead>
                        <tr class="bg-gray-100 border-b border-gray-200">
                            <th class="px-4 py-3 text-left text-green-500">Contact Method</th>
                            <th class="px-4 py-3 text-left text-green-500">Contact Details</th>
                            <th class="px-4 py-3 text-center w-16 text-green-500">Active</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="border-b border-gray-200">
                            <td class="px-4 py-3">Post</td>
                            <td class="px-4 py-3">
                                <div v-if="userContactDetails.address">
                                    {{ formatAddress }}
                                </div>
                                <div v-else>
                                    <span class="text-gray-500 italic">Address not available</span>
                                </div>
                            </td>
                            <td class="px-4 py-3 text-center">
                                <asli-checkbox
                                    id="contact_post"
                                    name="contact_post"
                                    v-model="marketingPreferences.contact_preferences.post"
                                    :disabled="!userContactDetails.address"
                                    :hide-label="true"
                                />
                            </td>
                        </tr>
                        <tr class="border-b border-gray-200">
                            <td class="px-4 py-3">Email</td>
                            <td class="px-4 py-3">
                                <span v-if="userContactDetails.email">{{ userContactDetails.email }}</span>
                                <span v-else class="text-gray-500 italic">Email not available</span>
                            </td>
                            <td class="px-4 py-3 text-center">
                                <asli-checkbox
                                    id="contact_email"
                                    name="contact_email"
                                    v-model="marketingPreferences.contact_preferences.email"
                                    :disabled="!userContactDetails.email"
                                    :hide-label="true"
                                />
                            </td>
                        </tr>
                        <tr class="border-b border-gray-200">
                            <td class="px-4 py-3">SMS</td>
                            <td class="px-4 py-3">
                                <span v-if="userContactDetails.mobile">{{ userContactDetails.mobile }}</span>
                                <span v-else class="text-gray-500 italic">Mobile number not available</span>
                            </td>
                            <td class="px-4 py-3 text-center">
                                <asli-checkbox
                                    id="contact_sms"
                                    name="contact_sms"
                                    v-model="marketingPreferences.contact_preferences.sms"
                                    :disabled="!userContactDetails.mobile"
                                    :hide-label="true"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3">Mobile</td>
                            <td class="px-4 py-3">
                                <span v-if="userContactDetails.mobile">{{ userContactDetails.mobile }}</span>
                                <span v-else class="text-gray-500 italic">Mobile number not available</span>
                            </td>
                            <td class="px-4 py-3 text-center">
                                <asli-checkbox
                                    id="contact_mobile"
                                    name="contact_mobile"
                                    v-model="marketingPreferences.contact_preferences.mobile"
                                    :disabled="!userContactDetails.mobile"
                                    :hide-label="true"
                                />
                            </td>
                        </tr>
                        <tr class="bg-gray-50">
                            <td colspan="2" class="px-4 py-3 text-right">
                                <span class="font-medium">Select All</span>
                            </td>
                            <td class="px-4 py-3 text-center">
                                <asli-checkbox
                                    id="contact_select_all"
                                    name="contact_select_all"
                                    v-model="selectAllContacts"
                                    :hide-label="true"
                                    @update:modelValue="toggleAllContacts"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </form-transition>
        <asli-error v-if="showError" class="my-4">
            <asli-icon icon="warning" class="text-red-500 mr-1" />
            <span class="text-red-500">Please select at least one contact method.</span>
        </asli-error>
        <button-bar>
            <asli-button id="form-back" color="grey" type="button" @click="goBack">Back</asli-button>
            <asli-button id="form-save" color="primary" type="submit"> Save Preferences </asli-button>
        </button-bar>
    </asli-form>
</template>

<script lang="ts" setup>
import { AsliForm, AsliRadio, AsliCheckbox, AsliButton, AsliSectionHeader, AsliError } from "@mbs-npm/anpost-ui";
import { reactive, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import ButtonBar from "@/components/ButtonBar.vue";
import FormTransition from "@/components/FormTransitions/FormTransition.vue";
import { useProposalStore } from "@/stores/risk/risk";
import { ROUTE_NAMES } from "@/router/types";
import { useListStore } from "@/stores/lists/lists";

const router = useRouter();
const proposalStore = useProposalStore();
const listStore = useListStore();
const lists = computed(() => listStore?.lists);

const selectAllContacts = ref(false);
const showError = ref(false);

const marketingPreferences = reactive({
    allow_general_marketing: proposalStore.proposal.allow_general_marketing,
    allow_product_marketing: proposalStore.proposal.allow_product_marketing,
    contact_preferences: {
        post: proposalStore.proposal.contact_preferences?.post || false,
        email: proposalStore.proposal.contact_preferences?.email || false,
        sms: proposalStore.proposal.contact_preferences?.sms || false,
        mobile: proposalStore.proposal.contact_preferences?.mobile || false
    }
});

const userContactDetails = reactive({
    email: proposalStore.proposal.risk.proposer.email || "",
    mobile: proposalStore.proposal.risk.proposer.mobile_phone_number || "",
    address: {
        address_line_1: proposalStore.proposal.risk.proposer?.address?.address_line1 || "",
        address_line_2: proposalStore.proposal.risk.proposer?.address?.address_line2 || "",
        address_line_3: proposalStore.proposal.risk.proposer?.address?.address_line3 || "",
        address_line_4: proposalStore.proposal.risk.proposer?.address?.address_line4 || ""
    }
});

const formatAddress = computed(() => {
    const { address_line_1, address_line_2, address_line_3, address_line_4 } = userContactDetails.address;
    return [address_line_1, address_line_2, address_line_3, address_line_4].filter(Boolean).join(", ");
});

const formIsValid = computed(() => {
    if (
        marketingPreferences.allow_general_marketing === null ||
        (marketingPreferences.allow_general_marketing === false &&
            marketingPreferences.allow_product_marketing === null)
    )
        return false;

    if (
        marketingPreferences.allow_general_marketing === true ||
        marketingPreferences.allow_product_marketing === true
    ) {
        return (
            marketingPreferences.contact_preferences.post ||
            marketingPreferences.contact_preferences.email ||
            marketingPreferences.contact_preferences.sms ||
            marketingPreferences.contact_preferences.mobile
        );
    }

    return true;
});

watch(
    () => [marketingPreferences.allow_general_marketing, marketingPreferences.allow_product_marketing],
    () => {
        if (!marketingPreferences.allow_general_marketing && !marketingPreferences.allow_product_marketing) {
            marketingPreferences.contact_preferences.post = false;
            marketingPreferences.contact_preferences.email = false;
            marketingPreferences.contact_preferences.sms = false;
            marketingPreferences.contact_preferences.mobile = false;
        }
    },
    { immediate: true }
);

watch(
    () => [
        marketingPreferences.contact_preferences.post,
        marketingPreferences.contact_preferences.email,
        marketingPreferences.contact_preferences.sms,
        marketingPreferences.contact_preferences.mobile
    ],
    () => {
        selectAllContacts.value =
            marketingPreferences.contact_preferences.post &&
            marketingPreferences.contact_preferences.email &&
            marketingPreferences.contact_preferences.sms &&
            marketingPreferences.contact_preferences.mobile;

        showError.value = false;
    },
    { immediate: true }
);

function toggleAllContacts(value: boolean) {
    marketingPreferences.contact_preferences.post = value;
    marketingPreferences.contact_preferences.email = value;
    marketingPreferences.contact_preferences.sms = value;
    marketingPreferences.contact_preferences.mobile = value;
}

function saveMarketingPreferences() {
    proposalStore.patchMarketingPreferences(marketingPreferences);
}

async function submit() {
    if (formIsValid.value) {
        showError.value = false;
        saveMarketingPreferences();
        await proposalStore.updateMarketingPreferences();
    } else {
        showError.value = true;
    }
}

function goBack() {
    router.push({ name: ROUTE_NAMES.MarketingPreferences });
}
</script>
